import type { ProfileProgress } from "../progression/levels";
import type { OrbitForgeSave, PaidSupplyCrateId, SupplyCrateCosmeticReward, SupplyCrateId, SupplyCrateResult } from "../save/types";
import {
  FREE_SUPPLY_COOLDOWN_SECONDS,
  PAID_CRATE_WINDOW_LIMIT,
  PAID_CRATE_WINDOW_SECONDS,
  SUPPLY_CRATE_UNLOCK_LEVEL,
  getPaidSupplyCrateDefinition,
  paidSupplyCrateDefinitions,
  paidSupplyCrateIds
} from "./catalog";
import { calculateFreeSupplyDropValue, calculatePaidSupplyCratePrice } from "./pricing";

export interface SupplyCrateCardView {
  readonly id: PaidSupplyCrateId;
  readonly name: string;
  readonly rewardLine: string;
  readonly price: number;
  readonly affordable: boolean;
  readonly remainingOpens: number;
  readonly resetsInSeconds: number;
  readonly canOpen: boolean;
  readonly locked: boolean;
}

export interface FreeSupplyDropView {
  readonly ready: boolean;
  readonly remainingSeconds: number;
  readonly valueFloor: number;
  readonly canOpen: boolean;
}

export interface SupplyCrateResultView {
  readonly result: SupplyCrateResult;
  readonly crateName: string;
  readonly presented: boolean;
  readonly rewardLines: readonly string[];
}

export interface SupplyCratesPanelView {
  readonly unlocked: boolean;
  readonly pendingBlocksOpen: boolean;
  readonly freeDrop: FreeSupplyDropView;
  readonly paidCrates: readonly SupplyCrateCardView[];
  readonly pendingResult: SupplyCrateResultView | null;
  readonly lastResult: SupplyCrateResultView | null;
}

export function buildSupplyCratesView(save: OrbitForgeSave, progress: ProfileProgress, nowUnixSeconds: number): SupplyCratesPanelView {
  const unlocked = progress.effectiveLevel >= SUPPLY_CRATE_UNLOCK_LEVEL;
  const pendingBlocksOpen = save.crates.pendingResult !== null;
  return {
    unlocked,
    pendingBlocksOpen,
    freeDrop: buildFreeDropView(save, progress, nowUnixSeconds, unlocked, pendingBlocksOpen),
    paidCrates: paidSupplyCrateIds.map((crateId) => buildPaidCrateView(save, progress, nowUnixSeconds, crateId, unlocked, pendingBlocksOpen)),
    pendingResult: save.crates.pendingResult ? buildResultView(save.crates.pendingResult) : null,
    lastResult: save.crates.lastResult ? buildResultView(save.crates.lastResult) : null
  };
}

export function crateDisplayName(crateId: SupplyCrateId): string {
  if (crateId === "free") {
    return "FREE SUPPLY DROP";
  }
  return getPaidSupplyCrateDefinition(crateId).name;
}

function buildFreeDropView(
  save: OrbitForgeSave,
  progress: ProfileProgress,
  nowUnixSeconds: number,
  unlocked: boolean,
  pendingBlocksOpen: boolean
): FreeSupplyDropView {
  const remainingSeconds = Math.max(0, save.crates.freeReadyAtUnixSeconds - nowUnixSeconds);
  return {
    ready: remainingSeconds === 0,
    remainingSeconds,
    valueFloor: calculateFreeSupplyDropValue(save, progress),
    canOpen: unlocked && !pendingBlocksOpen && remainingSeconds === 0
  };
}

function buildPaidCrateView(
  save: OrbitForgeSave,
  progress: ProfileProgress,
  nowUnixSeconds: number,
  crateId: PaidSupplyCrateId,
  unlocked: boolean,
  pendingBlocksOpen: boolean
): SupplyCrateCardView {
  const definition = getPaidSupplyCrateDefinition(crateId);
  const price = calculatePaidSupplyCratePrice(crateId, save, progress);
  const window = save.crates.paidOpenWindows[crateId];
  const expired = nowUnixSeconds - window.startUnixSeconds >= PAID_CRATE_WINDOW_SECONDS;
  const opens = expired ? 0 : window.opens;
  const resetsInSeconds = expired || opens === 0 ? PAID_CRATE_WINDOW_SECONDS : Math.max(0, window.startUnixSeconds + PAID_CRATE_WINDOW_SECONDS - nowUnixSeconds);
  const remainingOpens = Math.max(0, PAID_CRATE_WINDOW_LIMIT - opens);

  return {
    id: crateId,
    name: definition.name,
    rewardLine: rewardLine(definition),
    price,
    affordable: save.stats.Credits >= price,
    remainingOpens,
    resetsInSeconds,
    canOpen: unlocked && !pendingBlocksOpen && remainingOpens > 0 && save.stats.Credits >= price,
    locked: !unlocked
  };
}

function buildResultView(result: SupplyCrateResult): SupplyCrateResultView {
  return {
    result,
    crateName: crateDisplayName(result.crateId),
    presented: result.presentedAtUnixSeconds !== null,
    rewardLines: resultRewardLines(result)
  };
}

function rewardLine(definition: (typeof paidSupplyCrateDefinitions)[number]): string {
  const coreRange = definition.coreCountRange[0] === definition.coreCountRange[1] ? definition.coreCountRange[0] : `${definition.coreCountRange[0]}-${definition.coreCountRange[1]}`;
  return `${Math.round(definition.cashRange[0] * 100)}-${Math.round(definition.cashRange[1] * 100)}% CR | ${coreRange} CORES`;
}

function resultRewardLines(result: SupplyCrateResult): readonly string[] {
  const lines = [`+${result.cash} BONUS CR`];
  if (result.jackpot) {
    lines.push("JACKPOT VAULT HIT");
  } else if (result.lucky) {
    lines.push("LUCKY SUPPLY DROP");
  }
  if (result.ticketSeconds) {
    lines.push(`+${result.ticketSeconds}s AUTO TICKET`);
  } else if (result.ticketFallbackCash > 0) {
    lines.push("TICKET STORAGE FULL");
  }
  if (result.coreBurstCount > 0) {
    lines.push(coreSpawnRewardLine(result.coreBurstCount));
  }
  if (result.overchargedCoreMultiplier) {
    lines.push(`OVERCHARGED x${result.overchargedCoreMultiplier}`);
  }
  if (result.cosmetic) {
    lines.push(cosmeticRewardLine(result.cosmetic));
    if (!result.cosmetic.duplicate) {
      lines.push("AUTO-EQUIPPED");
    }
  }
  return lines;
}

function coreSpawnRewardLine(count: number): string {
  return `SPAWNED ${count} ADDITIONAL ${count === 1 ? "CORE" : "CORES"}`;
}

function cosmeticRewardLine(cosmetic: SupplyCrateCosmeticReward): string {
  const family = cosmetic.kind === "corePattern" ? "CORE PATTERN" : "SHELL SKIN";
  const action = cosmetic.duplicate ? "DUPLICATE" : "UNLOCKED";
  return `${action} ${family}: ${cosmetic.label.toUpperCase()}`;
}

export function freeDropCooldownProgress(save: OrbitForgeSave, nowUnixSeconds: number): number {
  const elapsed = FREE_SUPPLY_COOLDOWN_SECONDS - Math.max(0, save.crates.freeReadyAtUnixSeconds - nowUnixSeconds);
  return Math.min(1, Math.max(0, elapsed / FREE_SUPPLY_COOLDOWN_SECONDS));
}
